import React, { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export const PRICING_FALLBACK = {
  premium_monthly: { amount: 9.99 }, premium_yearly: { amount: 101.88 },
  super_premium_monthly: { amount: 17.99 }, super_premium_yearly: { amount: 183.48 },
};

const PLANS = [
  { id: "free", title: "Free", tagline: "Try it on your next job",
    features: ["Up to 2 projects", "AI milestone blueprints", "Client portal with e-signature", "Branded PDF invoices", "Automatic email reminders", "15 AI assistant messages / day"] },
  { id: "premium", title: "Premium", tagline: "For solo pros", monthlyKey: "premium_monthly", yearlyKey: "premium_yearly",
    features: ["Everything in Free", "Unlimited projects", "Unlimited AI business assistant", "Public portfolio + lead inbox", "Priority support"] },
  { id: "super_premium", title: "Super Premium", tagline: "For busy crews", monthlyKey: "super_premium_monthly", yearlyKey: "super_premium_yearly", highlighted: true,
    features: ["Everything in Premium", "Change orders your client signs", "“Pay now” button + payment instructions on portal & invoices", "Your brand colour, no ScopeGuard branding", "Daily reminders until signed", "Alert when your client opens the link", "One-tap text reminders from your own phone", "Income export for taxes (CSV)"] },
];

function PriceBlock({ plan, pricing, annual }) {
  if (plan.id === "free") {
    return (<div><span className="font-display text-4xl font-bold">$0</span><span className="text-sm text-muted-foreground ml-1">forever</span>
      <div className="text-xs text-muted-foreground opacity-70 mt-1">No card required</div></div>);
  }
  const m = pricing[plan.monthlyKey]?.amount || 0;
  const y = pricing[plan.yearlyKey]?.amount || 0;
  if (annual) {
    return (
      <div data-testid={`price-${plan.id}-yearly`}>
        <span className="font-display text-4xl font-bold tabular-nums">${y.toFixed(2)}</span>
        <span className="text-sm text-muted-foreground ml-1">/ year</span>
        <div className="text-xs text-muted-foreground opacity-70 mt-1 tabular-nums">
          ${(y / 12).toFixed(2)}/mo · you save ${(m * 12 - y).toFixed(2)}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`price-${plan.id}-monthly`}>
      <span className="font-display text-4xl font-bold tabular-nums">${m.toFixed(2)}</span>
      <span className="text-sm text-muted-foreground ml-1">/ month</span>
      <div className="text-xs text-muted-foreground opacity-70 mt-1">Cancel any time</div>
    </div>
  );
}

function ctaLabel(plan, currentPlan, isOwner, trial) {
  if (isOwner) return "Included with Owner";
  if (plan.id === currentPlan) return "Current plan";
  if (plan.id === "free") return "Start free";
  if (trial && plan.id === "super_premium") return "Start 7-day free trial";
  return `Upgrade to ${plan.title}`;
}

export function PlanCards({ pricing = PRICING_FALLBACK, showFree = false, currentPlan, isOwner, onSelect, trial = false }) {
  const [annual, setAnnual] = useState(false);
  const plans = showFree ? PLANS : PLANS.filter((p) => p.id !== "free");
  return (
    <div>
      <div className="inline-flex items-center gap-1 border border-border rounded-full p-1 bg-card mb-6" data-testid="billing-toggle">
        <button data-testid="billing-monthly-btn" onClick={() => setAnnual(false)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-indigo-600 text-white" : "text-muted-foreground"}`}>Monthly</button>
        <button data-testid="billing-yearly-btn" onClick={() => setAnnual(true)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${annual ? "bg-indigo-600 text-white" : "text-muted-foreground"}`}>
          Yearly <span className={annual ? "text-indigo-100" : "text-emerald-600 dark:text-emerald-400"}>−15%</span>
        </button>
      </div>
      <div className={`grid gap-6 ${showFree ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {plans.map((plan) => {
          const disabled = isOwner || plan.id === currentPlan;
          const key = plan.id === "free" ? "free" : annual ? plan.yearlyKey : plan.monthlyKey;
          return (
            <div key={plan.id} data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-xl bg-card p-6 flex flex-col ${plan.highlighted ? "border-2 border-indigo-500" : "border border-border"}`}>
              {plan.highlighted && <div className="absolute -top-3 left-6 bg-indigo-600 text-white text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded">{trial ? "Most popular · 7 days free" : "Most popular"}</div>}
              <div className="font-display text-xl font-bold">{plan.title}</div>
              <div className="text-sm text-muted-foreground mb-4">{plan.tagline}</div>
              <PriceBlock plan={plan} pricing={pricing} annual={annual} />
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button data-testid={`plan-cta-${plan.id}`} disabled={disabled} onClick={() => onSelect && onSelect(key, plan.id)}
                className={`w-full mt-6 h-11 font-semibold ${plan.highlighted ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900"}`}>
                {ctaLabel(plan, currentPlan, isOwner, trial)}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
